import React, {useEffect, useRef, useState} from 'react'
import {createPortal} from "react-dom";
import {Button} from "primereact/button";
import FetchUtil from "../../../../util/FetchUtil";
import ConfigUtil from "../../../../util/ConfigUtil";
import {vorhanden} from "./statistikWerte";

const ERGEBNISSE = {
    SIEG: {label: "Sieg", klasse: "dhpt-ss-sieg"},
    UNENTSCHIEDEN: {label: "Unentschieden", klasse: "dhpt-ss-unentschieden"},
    NIEDERLAGE: {label: "Niederlage", klasse: "dhpt-ss-niederlage"},
    FREILOS: {label: "Freilos", klasse: "dhpt-ss-freilos"},
};

/**
 * Erklärt im neuen Design, wie Platz, Punkte und Buchholz eines Spielers im Schweizer System zustande kommen.
 * Ersetzt in der neuen Ansicht den SchweizerSystemSortierungDialog; Daten wie dort über schweizersystem/protokoll.
 *
 * @param playerId optional, Spieler der direkt angezeigt wird (z.B. der markierte Spieler)
 */
export default function SchweizerSortierungDialog({tournamentId, players, playerId, onClose}) {

    const [spielerId, setSpielerId] = useState(playerId);
    const [protokoll, setProtokoll] = useState(undefined);
    const [fehler, setFehler] = useState(undefined);
    const [laedt, setLaedt] = useState(false);
    const [kopiert, setKopiert] = useState(undefined);
    const inhaltRef = useRef(null);

    useEffect(() => {
        // die Seite dahinter soll nicht mitscrollen
        const overflowVorher = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = overflowVorher;
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        setProtokoll(undefined);
        setFehler(undefined);
        setKopiert(undefined);
        if (!vorhanden(spielerId) || spielerId === "") {
            return;
        }
        setLaedt(true);
        if (inhaltRef.current) {
            inhaltRef.current.scrollTop = 0;
        }
        FetchUtil.fetchGet(ConfigUtil.getConfig().resourceUrls.public + "/tournament/" + tournamentId + "/schweizersystem/protokoll/" + spielerId,
            json => {
                setProtokoll(json);
                setLaedt(false);
            },
            responseNotOk => {
                setFehler("Fehler beim Laden des Protokolls: " + responseNotOk.message);
                setLaedt(false);
            },
            error => {
                setFehler("Fehler beim Laden des Protokolls: " + error.message);
                setLaedt(false);
            });
    }, [tournamentId, spielerId]);

    const spielerListe = [...(players || [])].sort((a, b) => a.name.localeCompare(b.name, "de"));

    const kopiereProtokoll = () => {
        navigator.clipboard.writeText(protokoll.protokoll.join("\n"))
            .then(() => setKopiert("Protokoll wurde in die Zwischenablage kopiert."))
            .catch(() => setKopiert("Protokoll konnte nicht kopiert werden."));
    }

    const getInhalt = () => {
        if (fehler) {
            return <p className="dhpt-hinweis dhpt-gd-fehler" role="alert">{fehler}</p>;
        }
        if (laedt) {
            return <p className="dhpt-hinweis">Protokoll wird geladen …</p>;
        }
        if (!protokoll) {
            return <p className="dhpt-hinweis">Wähle einen Spieler, um zu sehen, wie sein Platz in der Tabelle zustande kommt.</p>;
        }
        return <>
            <Kopf protokoll={protokoll}/>
            <Werte protokoll={protokoll}/>
            <div className="dhpt-karten">
                <Sortierung protokoll={protokoll}/>
                <Punktevergabe protokoll={protokoll}/>
            </div>
            <Berechnung protokoll={protokoll} onSpielerWaehlen={setSpielerId}/>
            <Spiele protokoll={protokoll} onSpielerWaehlen={setSpielerId}/>
            {protokoll.protokoll && protokoll.protokoll.length > 0 &&
                <details className="dhpt-karte dhpt-ss-details">
                    <summary className="dhpt-karte-titel">Komplettes Protokoll als Text</summary>
                    <div className="dhpt-ss-text-leiste">
                        <Button icon="pi pi-copy" label="Kopieren" className="p-button-sm p-button-outlined dhpt-button-hell" onClick={kopiereProtokoll}/>
                        {kopiert && <span className="dhpt-hinweis" role="status">{kopiert}</span>}
                    </div>
                    <pre className="dhpt-ss-text">{protokoll.protokoll.join("\n")}</pre>
                </details>
            }
        </>
    }

    const dialog = <div className="dhpt-vollbild" role="dialog" aria-modal="true" aria-label="Sortierung im Schweizer System">
        <div className="dhpt-vollbild-leiste">
            <span className="dhpt-vollbild-titel">Sortierung im Schweizer System</span>
            <Button icon="pi pi-times" label="Schließen" className="p-button-sm p-button-secondary" onClick={() => onClose()}/>
        </div>
        <div className="dhpt-vollbild-inhalt" ref={inhaltRef}>
            <div className="dhpt-st-dialog">
                <label className="dhpt-st-auswahl dhpt-ss-auswahl">
                    <span className="dhpt-st-versteckt">Spieler</span>
                    <select value={vorhanden(spielerId) ? spielerId : ""} onChange={(event) => setSpielerId(event.target.value)}>
                        <option value="">Spieler wählen …</option>
                        {spielerListe.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </label>
                {getInhalt()}
            </div>
        </div>
    </div>;

    // direkt in die Seite hängen, damit der Dialog nicht von umgebenden Containern (Zoom, Vollbild) beeinflusst wird
    const ziel = document.querySelector(".dhpt-seite");
    return ziel ? createPortal(dialog, ziel) : dialog;
}

function Kopf({protokoll}) {
    return <section className="dhpt-karte dhpt-ss-kopf">
        <div className="dhpt-ss-name">
            {protokoll.inaktiv ? <s>{protokoll.playerName}</s> : protokoll.playerName}
            {protokoll.inaktiv && <span className="dhpt-chip">inaktiv</span>}
        </div>
        <div className="dhpt-ss-platz">
            <span className="dhpt-ss-platz-zahl">Platz {protokoll.platz}</span>
            <span className="dhpt-hinweis">von {protokoll.anzahlSpieler} in Gruppe {protokoll.gruppe}</span>
        </div>
    </section>
}

function Werte({protokoll}) {
    const kennzahl = (wert, bezeichnung, haupt) => {
        return <div key={bezeichnung} className={"dhpt-kennzahl" + (haupt ? " dhpt-ss-haupt" : "")}><b>{wert}</b><small>{bezeichnung}</small></div>
    }
    return <div className="dhpt-kennzahlen">
        {kennzahl(protokoll.punkte, "Punkte", true)}
        {protokoll.buchholzWertungAktiv && kennzahl(protokoll.buchholz, "Buchholz", true)}
        {protokoll.buchholzWertungAktiv && kennzahl(protokoll.feinBuchholz, "Feinbuchholz", true)}
        {kennzahl(protokoll.gewonneneSpiele + " / " + protokoll.unentschiedeneSpiele + " / " + protokoll.verloreneSpiele, "S / U / N")}
        {kennzahl(protokoll.legsGewonnen + " : " + protokoll.legsVerloren, "Legs")}
        {kennzahl(formatiereDifferenz(protokoll.legDifferenz), "Leg-Differenz")}
    </div>
}

function Sortierung({protokoll}) {
    return <section className="dhpt-karte">
        <h3 className="dhpt-karte-titel"><span>Sortierung der Tabelle</span></h3>
        <ol className="dhpt-ss-liste">
            {protokoll.sortierung.map((kriterium, index) => {
                const wert = getWertZuKriterium(protokoll, kriterium);
                return <li key={kriterium}>
                    <span className="dhpt-ss-nummer">{index + 1}</span>
                    <span className="dhpt-ss-liste-text">{kriterium}</span>
                    <b className="dhpt-ss-liste-wert">{vorhanden(wert) ? wert : "–"}</b>
                </li>
            })}
        </ol>
        {!protokoll.buchholzWertungAktiv && <p className="dhpt-hinweis dhpt-ss-fussnote">Die Buchholz-Wertung ist für dieses Turnier nicht aktiv.</p>}
    </section>
}

function Punktevergabe({protokoll}) {
    const eintraege = protokoll.punktevergabeEintraege;
    return <section className="dhpt-karte">
        <h3 className="dhpt-karte-titel"><span>Punktevergabe</span></h3>
        {eintraege && eintraege.length > 0 ?
            <ul className="dhpt-ss-liste">
                {eintraege.map((eintrag, index) => <li key={"pv_" + index}>
                    <span className="dhpt-ss-liste-text">{eintrag.bezeichnung}</span>
                    {vorhanden(eintrag.wert) && <b className="dhpt-ss-liste-wert">{eintrag.wert}</b>}
                </li>)}
            </ul>
            : <p className="dhpt-ss-absatz">{protokoll.punktevergabe}</p>}
    </section>
}

function Berechnung({protokoll, onSpielerWaehlen}) {
    const spiele = sortiereSpiele(protokoll.spiele).filter(spiel => spiel.zaehltFuerWertung);
    const buchholz = protokoll.buchholzWertungAktiv;
    const plus = (wert) => vorhanden(wert) ? "+" + wert : "–";
    return <section className="dhpt-karte">
        <h3 className="dhpt-karte-titel"><span>Berechnung</span></h3>
        <div className="dhpt-gd-scroll">
            <table className="dhpt-tab dhpt-ss-tab">
                <thead>
                <tr>
                    <th className="dhpt-tab-zahl">Runde</th>
                    <th>Gegner</th>
                    <th className="dhpt-tab-zahl">Punkte</th>
                    {buchholz && <th className="dhpt-tab-zahl" title="Aktuelle Punkte des Gegners">Buchholz</th>}
                    {buchholz && <th className="dhpt-tab-zahl" title="Aktuelle Buchholzzahl des Gegners">Feinbuchholz</th>}
                </tr>
                </thead>
                <tbody>
                {spiele.map(spiel => <tr key={"b_" + spiel.runde + "_" + spiel.spielnummer} className="dhpt-tab-zeile">
                    <td className="dhpt-tab-zahl">{spiel.runde}</td>
                    <td><GegnerName spiel={spiel} onSpielerWaehlen={onSpielerWaehlen}/></td>
                    <td className="dhpt-tab-zahl">+{spiel.punkte}</td>
                    {buchholz && <td className="dhpt-tab-zahl">{plus(spiel.gegnerPunkte)}</td>}
                    {buchholz && <td className="dhpt-tab-zahl">{plus(spiel.gegnerBuchholz)}</td>}
                </tr>)}
                <tr className="dhpt-ss-summe">
                    <td/>
                    <td>Summe</td>
                    <td className="dhpt-tab-zahl">{protokoll.punkte}</td>
                    {buchholz && <td className="dhpt-tab-zahl">{protokoll.buchholz}</td>}
                    {buchholz && <td className="dhpt-tab-zahl">{protokoll.feinBuchholz}</td>}
                </tr>
                </tbody>
            </table>
        </div>
        {buchholz &&
            <p className="dhpt-hinweis dhpt-ss-fussnote">
                Buchholz = Summe der aktuellen Punkte aller Gegner. Feinbuchholz = Summe der aktuellen Buchholzzahlen aller Gegner.
            </p>
        }
    </section>
}

function Spiele({protokoll, onSpielerWaehlen}) {
    const spiele = sortiereSpiele(protokoll.spiele);
    const buchholz = protokoll.buchholzWertungAktiv;
    return <section className="dhpt-karte">
        <h3 className="dhpt-karte-titel"><span>Spiele</span><span className="dhpt-karte-zusatz">{spiele.length}</span></h3>
        {spiele.length === 0 && <p className="dhpt-hinweis">Keine Spiele vorhanden.</p>}
        <ul className="dhpt-ss-spiele">
            {spiele.map(spiel => {
                const ergebnis = getErgebnis(spiel);
                return <li key={"s_" + spiel.runde + "_" + spiel.spielnummer}
                           className={"dhpt-ss-spiel " + ergebnis.klasse + (spiel.zaehltFuerWertung ? "" : " dhpt-ss-zaehlt-nicht")}>
                    <div className="dhpt-ss-spiel-kopf">
                        <span className="dhpt-fett">Runde {spiel.runde}</span>
                        <span className="dhpt-hinweis dhpt-ss-spielnummer">Spiel {spiel.spielnummer}</span>
                        <span className="dhpt-chip dhpt-ss-ergebnis">{ergebnis.label}</span>
                        <span className="dhpt-chip">+{spiel.punkte} Pkt.</span>
                    </div>
                    <div className="dhpt-ss-spiel-gegner">
                        <span className="dhpt-ss-spiel-name">
                            {spiel.freilos ? "Freilos" : <>gegen <b><GegnerName spiel={spiel} onSpielerWaehlen={onSpielerWaehlen}/></b></>}
                        </span>
                        {!spiel.freilos && <span className="dhpt-ss-legs">{spiel.legsEigene} : {spiel.legsGegner}</span>}
                    </div>
                    {!spiel.zaehltFuerWertung && <p className="dhpt-ss-warnung">Dieses Spiel zählt nicht für die Wertung.</p>}
                    <div className="dhpt-ss-begruendung">
                        <div>{spiel.punkteBegruendung}</div>
                        {buchholz && !spiel.freilos && (vorhanden(spiel.gegnerPunkte) || vorhanden(spiel.gegnerBuchholz)) &&
                            <div className="dhpt-ss-buchholz">
                                {vorhanden(spiel.gegnerPunkte) && <span>Punkte {spiel.gegnerName}: <b>{spiel.gegnerPunkte}</b> → Buchholz +{spiel.gegnerPunkte}</span>}
                                {vorhanden(spiel.gegnerBuchholz) && <span>Buchholz {spiel.gegnerName}: <b>{spiel.gegnerBuchholz}</b> → Feinbuchholz +{spiel.gegnerBuchholz}</span>}
                            </div>
                        }
                        {buchholz && spiel.freilos && spiel.buchholzBegruendung && <div className="dhpt-ss-buchholz">{spiel.buchholzBegruendung}</div>}
                        {buchholz && <Aufklappbereich titel={"So kommt " + spiel.gegnerName + " auf " + spiel.gegnerPunkte + " Punkte"} zeilen={spiel.gegnerSpiele}/>}
                        {buchholz && <Aufklappbereich titel={"So kommt " + spiel.gegnerName + " auf die Buchholzzahl " + spiel.gegnerBuchholz}
                                                      zeilen={spiel.gegnerBuchholzBestandteile}/>}
                    </div>
                </li>
            })}
        </ul>
    </section>
}

function GegnerName({spiel, onSpielerWaehlen}) {
    if (spiel.freilos) {
        return "Freilos";
    }
    const name = spiel.gegnerInaktiv ? <s>{spiel.gegnerName}</s> : spiel.gegnerName;
    if (!vorhanden(spiel.gegnerPlayerId)) {
        return name;
    }
    return <button type="button" className="dhpt-ss-gegner" title={"Protokoll von " + spiel.gegnerName + " anzeigen"}
                   onClick={() => onSpielerWaehlen(spiel.gegnerPlayerId)}>{name}</button>
}

function Aufklappbereich({titel, zeilen}) {
    if (!zeilen || zeilen.length === 0) {
        return null;
    }
    return <details className="dhpt-ss-aufklapp">
        <summary>{titel}</summary>
        <ul>
            {zeilen.map((zeile, index) => <li key={"z_" + index}>{zeile}</li>)}
        </ul>
    </details>
}

function getErgebnis(spiel) {
    if (spiel.freilos) {
        return ERGEBNISSE.FREILOS;
    }
    const ergebnis = ERGEBNISSE[spiel.ergebnis];
    return ergebnis !== undefined ? ergebnis : {label: spiel.ergebnis, klasse: ""};
}

function sortiereSpiele(spiele) {
    return [...(spiele || [])].sort((a, b) => a.runde - b.runde || a.spielnummer - b.spielnummer);
}

function formatiereDifferenz(wert) {
    return wert > 0 ? "+" + wert : wert;
}

function getWertZuKriterium(protokoll, kriterium) {
    switch (kriterium) {
        case "Punkte":
            return protokoll.punkte;
        case "Buchholz":
            return protokoll.buchholz;
        case "Feinbuchholz":
            return protokoll.feinBuchholz;
        case "Gewonnene Spiele":
            return protokoll.gewonneneSpiele;
        case "Leg-Differenz":
            return formatiereDifferenz(protokoll.legDifferenz);
        case "Gewonnene Legs":
            return protokoll.legsGewonnen;
        default:
            return undefined;
    }
}
